import React from 'react';
import { Icon } from '../../components/icons';
import { BLOCK_LABEL, PASS_PERCENT } from '../../model';
import type { WizardDraft } from '../../wizardStore';
import { minutesLabel, questionsLabel } from '../../words';

interface Props {
  draft: WizardDraft;
  saving: boolean;
  onChange: (patch: Partial<WizardDraft>) => void;
  onBack: () => void;
  onSave: () => void;
}

/** Шаг 3: сводка, «Кто сможет пройти этот тест?» и сохранение (макет v6). */
export const StepReview: React.FC<Props> = ({ draft, saving, onChange, onBack, onSave }) => {
  const checks = [
    'Название написано',
    `В тесте есть вопросы: ${draft.items.length}`,
    `Время указано: ${minutesLabel(draft.minutes)}`,
  ];
  return (
    <>
      <div className="tw-card tw-stack-sm">
        <h2>Проверьте и сохраните</h2>
        <dl className="tw-facts">
          <dt>Название</dt>
          <dd>{draft.title.trim()}</dd>
          <dt>Раздел</dt>
          <dd>{BLOCK_LABEL[draft.block]}</dd>
          <dt>Время на тест</dt>
          <dd>{minutesLabel(draft.minutes)}</dd>
          <dt>Вопросов</dt>
          <dd>{questionsLabel(draft.items.length)}</dd>
          <dt>Чтобы сдать тест</dt>
          <dd>{`${PASS_PERCENT}% верных ответов`}</dd>
        </dl>
        <p className="tw-help">Раздел потом изменить нельзя. Всё остальное можно поменять на странице теста.</p>
        <ul className="tw-checks">
          {checks.map((text) => (
            <li key={text} className="tw-check">
              <Icon name="check" />
              <span>{text}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="tw-card tw-stack-sm">
        <h2>Кто сможет пройти этот тест?</h2>
        <p className="tw-help">Это можно изменить в любой момент.</p>
        <label className={`tw-pick${draft.who === 'nobody' ? ' tw-pick-on' : ''}`}>
          <input type="radio" name="who-sees" checked={draft.who === 'nobody'} onChange={() => onChange({ who: 'nobody' })} />
          <span className="tw-pick-body">
            <span className="tw-pick-title">
              Пока никто — сначала я сам всё проверю
              {' '}
              <span className="tw-tag">Рекомендуем</span>
            </span>
            <span className="tw-pick-text">
              Тест сохранится в состоянии «Готовится». Сотрудники его не увидят. Откроете его, когда будете готовы.
            </span>
          </span>
        </label>
        <label className={`tw-pick${draft.who === 'all' ? ' tw-pick-on' : ''}`}>
          <input type="radio" name="who-sees" checked={draft.who === 'all'} onChange={() => onChange({ who: 'all' })} />
          <span className="tw-pick-body">
            <span className="tw-pick-title">Все сотрудники</span>
            <span className="tw-pick-text">Тест сразу появится у всех сотрудников, и они смогут его пройти.</span>
          </span>
        </label>
        <p className="tw-help">Чтобы дать тест только выбранным людям, сохраните его и нажмите «Дать тест сотрудникам».</p>
      </div>

      <div className="tw-foot">
        <button type="button" className="tw-btn" disabled={saving} onClick={onBack}>
          <Icon name="left" />
          Назад
        </button>
        <button type="button" className="tw-btn tw-btn-primary tw-btn-big" disabled={saving} data-guide="wizard-save" onClick={onSave}>
          <Icon name="check" />
          Сохранить тест
        </button>
      </div>
    </>
  );
};
